/** Lector de Server-Sent Events sobre `fetch`.
 *
 * `EventSource` no sirve: no admite POST ni cabeceras, y el chat manda un cuerpo JSON. Este
 * lector trocea por línea y respeta que un evento puede llegar partido entre dos chunks de red,
 * que es el fallo que aparece solo en producción y nunca en local.
 */
import type { Evento } from "./tipos";

export async function* parsearSSE(cuerpo: ReadableStream<Uint8Array>): AsyncGenerator<Evento> {
  const lector = cuerpo.getReader();
  const decodificador = new TextDecoder();
  let resto = "";
  try {
    for (;;) {
      const { done, value } = await lector.read();
      if (done) break;
      resto += decodificador.decode(value, { stream: true });
      let corte: number;
      // Un evento termina en línea vacía. Puede haber varios en un chunk, o ninguno completo.
      while ((corte = resto.indexOf("\n\n")) !== -1) {
        const bloque = resto.slice(0, corte);
        resto = resto.slice(corte + 2);
        const evento = leerBloque(bloque);
        if (evento) yield evento;
      }
    }
    const ultimo = leerBloque(resto);
    if (ultimo) yield ultimo;
  } finally {
    lector.releaseLock();
  }
}

function leerBloque(bloque: string): Evento | null {
  const datos: string[] = [];
  for (const linea of bloque.split("\n")) {
    if (linea.startsWith(":")) continue; // latido
    if (linea.startsWith("data:")) datos.push(linea.slice(5).trimStart());
  }
  if (!datos.length) return null;
  const crudo = datos.join("\n");
  try {
    return JSON.parse(crudo) as Evento;
  } catch (err) {
    // Este `catch` se tragaba el evento SIN DEJAR RASTRO, y costó semanas: el servidor mandaba
    // `NaN` —token que `JSON.parse` rechaza— y en pantalla simplemente no aparecía la tarjeta de
    // evidencia. La respuesta citaba `[ev:1]` contra una tarjeta inexistente y el paso se quedaba
    // colgado, sin un solo mensaje en consola que apuntara aquí. La causa ya está arreglada en el
    // servidor (`motor/base.py::sin_no_finitos`); esto es para que el PRÓXIMO evento malformado se
    // encuentre en segundos y no por el reporte de un usuario.
    console.error("[sse] evento descartado: JSON ilegible", { error: String(err), crudo: crudo.slice(0, 300) });
    return null;
  }
}
